"use client"
import { usePending, pendingCreatedAt, UNDO_WINDOW_MS } from "./pending-commits"
import { useIsMobile } from "@/components/editor/use-is-mobile"

/**
 * Minimal undo affordance (AHD-7494): a small "Undo ⌘Z" button pinned top-right
 * of the content column, with a depleting hairline that "hourglasses" away over
 * the undo window (Linear-style) instead of a ticking seconds counter — and no
 * "Approved <key>" label, which only repeated what the worklist already shows.
 *
 * The bar is pure CSS animation, so no interval/timer here. On a cross-locale
 * remount it resumes mid-window via a negative animation-delay derived from the
 * pending's createdAt, rather than restarting from full.
 *
 * On mobile (AHD-7504) the desktop top-right pin sits off-screen / behind the
 * bottom sheet, and ⌘Z is meaningless on a phone, so it renders as a bottom-
 * anchored snackbar floating above the sheet with a plain "Undo" label. Desktop
 * is byte-identical — the mobile branch is fully gated behind useIsMobile().
 */
export function UndoToast({ onUndo }: { onUndo: () => void }) {
  const pending = usePending()
  const isMobile = useIsMobile()
  if (!pending) return null

  const start = pendingCreatedAt()
  const elapsed = start == null ? 0 : Math.max(0, Date.now() - start)

  return (
    <div
      className={isMobile ? "ahead-undo-snackbar" : "ahead-undo-toast"}
      data-ahead-editor-ui
      role="status"
      aria-live="polite"
    >
      <button
        className={
          isMobile ? "ahead-undo-snackbar-btn" : "ahead-undo-toast-btn"
        }
        onClick={onUndo}
      >
        Undo {isMobile ? null : <kbd>⌘Z</kbd>}
      </button>
      <span
        className={
          isMobile ? "ahead-undo-snackbar-bar" : "ahead-undo-toast-bar"
        }
        aria-hidden="true"
        style={{
          animationDuration: `${UNDO_WINDOW_MS}ms`,
          animationDelay: `-${elapsed}ms`,
        }}
      />
    </div>
  )
}
